import { useState } from "react";
import {
  Alert,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Chip,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExtensionIcon from "@mui/icons-material/Extension";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import LoadingComponent from "../components/LoadingComponent.jsx";
import InfoRow from "../components/InfoRow.jsx";
import ChangePasswordModal from "../components/ChangePasswordModal.jsx";
import { useNavigate } from "react-router-dom";
import { useCompanyDetails } from "../hooks/useCompanyDetails.jsx";
import { useAuth } from "../hooks/useAuth.jsx";
import { useWarehouses } from "../hooks/useWarehouses.jsx";

function CompanyDetail() {
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [clipboard, setClipboard] = useState(null);
  const [copyMessage, setCopyMessage] = useState(null);
  const [openAddCourierDialog, setOpenAddCourierDialog] = useState(false);
  const [newCourier, setNewCourier] = useState("");
  const [newStandBy, setNewStandBy] = useState(false);
  const [newRequiresPickupRequests, setNewRequiresPickupRequests] = useState(false);
  const [savingCourier, setSavingCourier] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [courierError, setCourierError] = useState(null);

  // Pickup schedule dialog
  const [pickupScheduleDialog, setPickupScheduleDialog] = useState(false);
  const [pickupScheduleWarehouse, setPickupScheduleWarehouse] = useState(null);
  const [pickupScheduleForm, setPickupScheduleForm] = useState({
    all_day_pickup: false,
    am_pickup_from: "",
    am_pickup_to: "",
    pm_pickup_from: "",
    pm_pickup_to: "",
  });
  const [savingSchedule, setSavingSchedule] = useState(false);

  const { user: authUser } = useAuth();
  const hasFulfillmentPro = authUser?.admin_plan?.admin_permissions?.addons?.fulfillment_pro === true;

  const {
    company,
    loading,
    error,
    settingCouriers,
    availableCouriers,
    setLoading,
    setError,
    changeSellerPassword,
    downloadSellerWarehouses,
    createSettingCourier,
    updateSettingCourier,
    deleteSettingCourier,
  } = useCompanyDetails();

  const { updateWarehousePickupSchedule } = useWarehouses();

  const navigate = useNavigate();

  if (loading) return <LoadingComponent />;

  if (!company) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Regresar
        </Button>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No se encontraron datos de la compañía.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const handlePasswordChangeConfirm = async (newPassword) => {
    setLoading(true);
    setError(null);
    try {
      await changeSellerPassword(newPassword);
      setIsChangePasswordModalOpen(false);
      setClipboard({
        email: company?.seller?.user?.email,
        password: newPassword,
      });
      setCopyMessage("");
    } catch (err) {
      setError("Ocurrió un error al cambiar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  const copyCredentialsToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify({ ...clipboard }, null, 2),
      );
      setCopyMessage("¡Copiado!");
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenPickupSchedule = (wh) => {
    setPickupScheduleWarehouse(wh);
    setPickupScheduleForm({
      all_day_pickup: wh.all_day_pickup ?? false,
      am_pickup_from: wh.am_pickup_from ?? "",
      am_pickup_to: wh.am_pickup_to ?? "",
      pm_pickup_from: wh.pm_pickup_from ?? "",
      pm_pickup_to: wh.pm_pickup_to ?? "",
    });
    setPickupScheduleDialog(true);
  };

  const handleSavePickupSchedule = async () => {
    if (!pickupScheduleWarehouse) return;
    setSavingSchedule(true);
    try {
      await updateWarehousePickupSchedule(pickupScheduleWarehouse.id, pickupScheduleForm);
      setPickupScheduleDialog(false);
    } catch (_) {
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleToggleStandBy = async (sc) => {
    setTogglingId(sc.id);
    try {
      await updateSettingCourier(sc.id, {
        stand_by_enabled: !sc.configuration?.stand_by_enabled,
        requires_pickup_requests: sc.configuration?.requires_pickup_requests ?? false,
      });
    } catch (err) {
      setCourierError("Error al actualizar el stand-by");
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleRequiresPickupRequests = async (sc) => {
    setTogglingId(sc.id);
    try {
      await updateSettingCourier(sc.id, {
        stand_by_enabled: sc.configuration?.stand_by_enabled ?? false,
        requires_pickup_requests: !sc.configuration?.requires_pickup_requests,
      });
    } catch (err) {
      setCourierError("Error al actualizar solicitudes de retiro");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteSettingCourier = async (id) => {
    setDeletingId(id);
    try {
      await deleteSettingCourier(id);
    } catch (err) {
      setCourierError("Error al eliminar la configuración");
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddCourier = async () => {
    if (!newCourier) return;
    setSavingCourier(true);
    setCourierError(null);
    try {
      await createSettingCourier(newCourier, {
        stand_by_enabled: newStandBy,
        requires_pickup_requests: newRequiresPickupRequests,
      });
      setOpenAddCourierDialog(false);
      setNewCourier("");
      setNewStandBy(false);
    } catch (err) {
      const msg =
        err.response?.data?.setting_courier ||
        err.response?.data?.courier?.[0] ||
        "Error al crear la configuración";
      setCourierError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSavingCourier(false);
    }
  };

  return (
    <>
      {error && (
        <Alert sx={{ borderRadius: "0px !important" }} severity="error">
          {error}
        </Alert>
      )}

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Regresar
        </Button>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              <Card elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "2px !important",
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold">
                      {company.socialReason}
                    </Typography>
                    <Stack direction="column" alignItems="flex-end">
                      <Button
                        size="small"
                        onClick={() => setIsChangePasswordModalOpen(true)}
                      >
                        Cambiar contraseña
                      </Button>
                      {clipboard?.email && (
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon fontSize="small" />}
                          onClick={copyCredentialsToClipboard}
                        >
                          {copyMessage || "Copiar credenciales"}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                  <Divider sx={{ marginTop: "0px !important" }} />
                  <InfoRow mb={1} label="ID" children={company.id} />
                  <InfoRow mb={1} label="ID" children={company.fullName} />
                  <InfoRow
                    mb={1}
                    label="Email User"
                    children={company.seller?.user?.email}
                  />
                  <InfoRow mb={1} label="RUT" children={company.rut} />
                  <InfoRow
                    mb={1}
                    label="Plan"
                    children={
                      <Chip
                        label={company.plan}
                        size="small"
                        variant="outlined"
                      />
                    }
                  />
                  <InfoRow
                    mb={1}
                    label="Fecha Creación"
                    children={
                      company.createdAt
                        ? new Date(company.createdAt).toLocaleString()
                        : "Desconocida"
                    }
                  />
                  <InfoRow
                    mb={1}
                    label="Último Login"
                    children={
                      company.lastLogin
                        ? new Date(company.lastLogin).toLocaleString()
                        : "Desconocida"
                    }
                  />
                  <InfoRow mb={1} label="KAM Asignado" children={company.kam} />
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card
              elevation={0}
              sx={{ border: "1px solid #e0e0e0", height: "100%" }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Operarios ({company.operators?.length || 0})
                </Typography>
                {company.operators?.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Bodega</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {company.operators.map((operator) => (
                          <TableRow key={operator.id}>
                            <TableCell>{operator.id}</TableCell>
                            <TableCell>{operator.user?.email || "-"}</TableCell>
                            <TableCell>
                              {operator.warehouse?.name || "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2, fontStyle: "italic" }}
                  >
                    No hay operarios asignados.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: 0 }}>
          <Grid item xs={12} md={12}>
            <Card elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <LocalShippingIcon sx={{ mr: 1, color: "text.secondary" }} />
                  <Typography variant="h6" fontWeight="bold">
                    Bodegas ({company.warehouses?.length || 0})
                  </Typography>
                </Box>

                {company.warehouses?.length > 0 && (
                  <Box sx={{ display: "flex-end", alignItems: "right", mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadSellerWarehouses(company.seller.id)}
                    >
                      Descargar
                    </Button>
                  </Box>
                )}
                {company.warehouses?.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ bgcolor: "#FAFAFA", borderRadius: 2 }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Encargado</TableCell>
                          <TableCell>Contacto</TableCell>
                          <TableCell>Dirección</TableCell>
                          <TableCell>Horario retiro</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {company.warehouses.map((wh) => (
                          <TableRow key={wh.id} sx={{ bgcolor: "white" }}>
                            <TableCell>{wh.id}</TableCell>
                            <TableCell fontWeight="bold">{wh.name}</TableCell>
                            <TableCell>{wh.storekeeper_name || "-"}</TableCell>
                            <TableCell>
                              <Typography variant="caption" display="block">
                                {wh.storekeeper_phone}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {wh.storekeeper_email}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{ fontSize: "0.8rem", maxWidth: 200 }}
                            >
                              {wh.address
                                ? `${wh.address.street} ${wh.address.street_number}, ${wh.address.commune}`
                                : "-"}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {wh.all_day_pickup
                                    ? "Todo el día"
                                    : wh.am_pickup_from
                                    ? `${wh.am_pickup_from} - ${wh.am_pickup_to}`
                                    : "Sin config."}
                                </Typography>
                                <Tooltip title="Configurar horario de retiro">
                                  <IconButton size="small" onClick={() => handleOpenPickupSchedule(wh)}>
                                    <AccessTimeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay bodegas.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={12}>
            <Card elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <ExtensionIcon sx={{ mr: 1, color: "text.secondary" }} />
                  <Typography variant="h6" fontWeight="bold">
                    Integraciones Activas (
                    {company.setting_ecommerces?.length || 0})
                  </Typography>
                </Box>

                {company.setting_ecommerces &&
                company.setting_ecommerces.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ bgcolor: "#FAFAFA", borderRadius: 2 }}
                  >
                    <Table sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ color: "text.secondary", fontWeight: "bold" }}
                          >
                            Ecommerce
                          </TableCell>
                          <TableCell
                            sx={{ color: "text.secondary", fontWeight: "bold" }}
                          >
                            Tipo
                          </TableCell>
                          <TableCell
                            sx={{ color: "text.secondary", fontWeight: "bold" }}
                          >
                            Estado
                          </TableCell>
                          <TableCell
                            sx={{ color: "text.secondary", fontWeight: "bold" }}
                          >
                            Fecha Activación
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {company.setting_ecommerces.map(
                          (integration, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                bgcolor: "white",
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell component="th" scope="row">
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  alignItems="center"
                                >
                                  <Avatar
                                    variant="rounded"
                                    src={integration.image}
                                    alt={integration.name}
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      bgcolor: "transparent",
                                      border: "1px solid #eee",
                                      "& .MuiAvatar-img": {
                                        objectFit: "contain",
                                        p: 0.5,
                                      },
                                    }}
                                  >
                                    <ExtensionIcon color="action" />
                                  </Avatar>

                                  <Box>
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight="bold"
                                    >
                                      {integration.name}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={
                                    integration.type?.toUpperCase() || "API"
                                  }
                                  size="small"
                                  sx={{
                                    borderRadius: 1,
                                    fontWeight: "bold",
                                    fontSize: "0.7rem",
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#00E2BE",
                                  }}
                                >
                                  <CheckCircleIcon
                                    sx={{ fontSize: 16, mr: 0.5 }}
                                  />
                                  <Typography variant="body2" fontWeight="bold">
                                    Activo
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {integration.created_at
                                    ? new Date(
                                        integration.created_at,
                                      ).toLocaleString()
                                    : "Desconocida"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: "center", py: 5 }}>
                    <Typography>No hay integraciones</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          {hasFulfillmentPro && (
          <Grid item xs={12} md={12}>
            <Card elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <LocalShippingIcon
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <Typography variant="h6" fontWeight="bold">
                      Configuración Stand-by de Couriers (
                      {settingCouriers.length})
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setCourierError(null);
                      setNewCourier("");
                      setNewStandBy(false);
                      setNewRequiresPickupRequests(false);
                      setOpenAddCourierDialog(true);
                    }}
                  >
                    Agregar
                  </Button>
                </Box>

                {courierError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {courierError}
                  </Alert>
                )}

                {settingCouriers.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ bgcolor: "#FAFAFA", borderRadius: 2 }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Courier</TableCell>
                          <TableCell>Stand-by</TableCell>
                          <TableCell>Solicitudes de retiro</TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {settingCouriers.map((sc) => (
                          <TableRow key={sc.id} sx={{ bgcolor: "white" }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {sc.courier}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={
                                      sc.configuration?.stand_by_enabled ??
                                      false
                                    }
                                    onChange={() => handleToggleStandBy(sc)}
                                    disabled={togglingId === sc.id}
                                  />
                                }
                                label={
                                  sc.configuration?.stand_by_enabled
                                    ? "Activo"
                                    : "Inactivo"
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={
                                      sc.configuration?.requires_pickup_requests ??
                                      false
                                    }
                                    onChange={() => handleToggleRequiresPickupRequests(sc)}
                                    disabled={togglingId === sc.id}
                                  />
                                }
                                label={
                                  sc.configuration?.requires_pickup_requests
                                    ? "Activo"
                                    : "Inactivo"
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Eliminar">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      handleDeleteSettingCourier(sc.id)
                                    }
                                    disabled={deletingId === sc.id}
                                  >
                                    {deletingId === sc.id ? (
                                      <CircularProgress
                                        size={16}
                                        color="error"
                                      />
                                    ) : (
                                      <DeleteIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay configuraciones de courier. Agrega una con el botón
                    de arriba.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          )}
        </Grid>
      </Box>

      <Dialog
        open={openAddCourierDialog}
        onClose={() => setOpenAddCourierDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Agregar configuración de courier</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {courierError && (
              <Alert severity="error">{courierError}</Alert>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Courier</InputLabel>
              <Select
                value={newCourier}
                label="Courier"
                onChange={(e) => setNewCourier(e.target.value)}
              >
                {availableCouriers
                  .filter(
                    (ac) =>
                      !settingCouriers.some((sc) => sc.courier === ac.courier),
                  )
                  .map((ac) => (
                    <MenuItem key={ac.courier} value={ac.courier}>
                      {ac.courier}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={newStandBy}
                  onChange={(e) => setNewStandBy(e.target.checked)}
                />
              }
              label="Stand-by activo"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newRequiresPickupRequests}
                  onChange={(e) => setNewRequiresPickupRequests(e.target.checked)}
                />
              }
              label="Solicitudes de retiro activas"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddCourierDialog(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAddCourier}
            disabled={!newCourier || savingCourier}
          >
            {savingCourier ? <CircularProgress size={20} /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ChangePasswordModal
        open={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        onConfirm={handlePasswordChangeConfirm}
        loading={loading}
      />

      {/* Dialog: horario de retiro por bodega */}
      <Dialog
        open={pickupScheduleDialog}
        onClose={() => setPickupScheduleDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Horario de retiro — {pickupScheduleWarehouse?.name}
        </DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={pickupScheduleForm.all_day_pickup}
                onChange={(e) =>
                  setPickupScheduleForm((prev) => ({
                    ...prev,
                    all_day_pickup: e.target.checked,
                  }))
                }
              />
            }
            label="Todo el día (00:00 – 23:59)"
            sx={{ mb: 2, display: "flex" }}
          />
          {!pickupScheduleForm.all_day_pickup && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Franja mañana
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Desde</InputLabel>
                  <input
                    type="time"
                    value={pickupScheduleForm.am_pickup_from}
                    onChange={(e) =>
                      setPickupScheduleForm((prev) => ({ ...prev, am_pickup_from: e.target.value }))
                    }
                    style={{ padding: "8px", borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                  />
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Hasta</InputLabel>
                  <input
                    type="time"
                    value={pickupScheduleForm.am_pickup_to}
                    onChange={(e) =>
                      setPickupScheduleForm((prev) => ({ ...prev, am_pickup_to: e.target.value }))
                    }
                    style={{ padding: "8px", borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                  />
                </FormControl>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Franja tarde (opcional)
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Desde</InputLabel>
                  <input
                    type="time"
                    value={pickupScheduleForm.pm_pickup_from}
                    onChange={(e) =>
                      setPickupScheduleForm((prev) => ({ ...prev, pm_pickup_from: e.target.value }))
                    }
                    style={{ padding: "8px", borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                  />
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Hasta</InputLabel>
                  <input
                    type="time"
                    value={pickupScheduleForm.pm_pickup_to}
                    onChange={(e) =>
                      setPickupScheduleForm((prev) => ({ ...prev, pm_pickup_to: e.target.value }))
                    }
                    style={{ padding: "8px", borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                  />
                </FormControl>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickupScheduleDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={savingSchedule}
            onClick={handleSavePickupSchedule}
            startIcon={savingSchedule ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {savingSchedule ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CompanyDetail;
